<template>
	<div>
		<b-form-group label="Name" label-size="sm" class="mb-2">
			<b-form-input size="sm" v-model="layer.name" @change="$emit('save')" />
		</b-form-group>

		<template v-if="layer.type === 'image'">
			<b-form-group label="Image" label-size="sm" class="mb-2">
				<pipi-pupu-file accept="image/*" :value="layer.props.file || null" @input="onFile" />
			</b-form-group>
			<b-form-group label="Or image URL" label-size="sm" class="mb-0">
				<b-form-input size="sm" v-model="layer.props.src" @change="$emit('source')" />
			</b-form-group>
		</template>

		<template v-else-if="layer.type === 'video'">
			<b-form-group label="Video" label-size="sm" class="mb-2">
				<pipi-pupu-file accept="video/*" :value="layer.props.file || null" @input="onFile" />
			</b-form-group>
			<b-form-group label="Or video URL" label-size="sm" class="mb-2">
				<b-form-input size="sm" v-model="layer.props.src" @change="$emit('source')" />
			</b-form-group>
			<b-form-checkbox v-model="layer.props.muted" @change="$emit('save')">Muted</b-form-checkbox>
			<b-form-checkbox v-model="layer.props.loop" @change="$emit('save')">Loop</b-form-checkbox>
			<b-form-checkbox v-model="layer.props.autoplay" @change="$emit('save')">Play automatically</b-form-checkbox>
		</template>

		<template v-else-if="layer.type === 'text'">
			<b-form-group label="Text" label-size="sm" class="mb-2">
				<b-form-textarea size="sm" rows="3" v-model="layer.props.text" @change="$emit('save')" />
			</b-form-group>
			<b-form-group label="Font family" label-size="sm" class="mb-2">
				<google-font-input :value="layer.props.fontFamily || ''" @input="setProp('fontFamily', $event)" />
			</b-form-group>
			<b-form-group label="Font size" label-size="sm" class="mb-2">
				<b-form-input size="sm" type="number" v-model.number="layer.props.fontSize" @change="$emit('save')" />
			</b-form-group>
			<b-form-row class="mb-2">
				<b-col>
					<label class="small mb-0">Align</label>
					<b-button-group size="sm" class="d-flex">
						<b-button v-for="a in horizontalAligns" :key="a.value" v-b-tooltip.hover.bottom :title="a.title" :pressed="(layer.props.align || 'left') === a.value" variant="outline-secondary" @click="setProp('align', a.value)"><b-icon :icon="a.icon" /></b-button>
					</b-button-group>
				</b-col>
				<b-col>
					<label class="small mb-0">Vertical align</label>
					<b-button-group size="sm" class="d-flex">
						<b-button v-for="a in verticalAligns" :key="a.value" v-b-tooltip.hover.bottom :title="a.title" :pressed="(layer.props.verticalAlign || 'top') === a.value" variant="outline-secondary" @click="setProp('verticalAlign', a.value)"><b-icon :icon="a.icon" /></b-button>
					</b-button-group>
				</b-col>
			</b-form-row>
			<b-form-group label="Color" label-size="sm" class="mb-2">
				<brand-color-input :overlay="overlay" v-model="layer.props.color" @input="queueSave" />
			</b-form-group>
			<b-form-checkbox v-model="layer.props.bold" @change="$emit('save')">Bold</b-form-checkbox>
			<b-form-checkbox v-model="layer.props.italic" @change="$emit('save')">Italic</b-form-checkbox>
		</template>

		<template v-else-if="layer.type === 'color'">
			<b-form-group label="Fill" label-size="sm" class="mb-2">
				<brand-color-input :overlay="overlay" v-model="layer.props.fill" @input="queueSave" />
			</b-form-group>
			<b-form-group label="Opacity" label-size="sm" class="mb-0">
				<b-form-input type="range" min="0" max="1" step="0.05" v-model.number="layer.props.opacity" @change="$emit('save')" />
			</b-form-group>
		</template>

		<template v-else-if="layer.type === 'widget'">
			<p class="small text-muted mb-2">{{widgetLabel}} widget &mdash; this instance has its own settings.</p>
			<template v-if="layer.props.physics">
				<b-form-group label="Collide with" label-size="sm" class="mb-2">
					<b-form-select size="sm" v-model="layer.props.physics.collisionMode" :options="collisionModes" @change="$emit('save')" />
				</b-form-group>
				<b-form-group v-if="layer.props.physics.collisionMode === 'blacklist'" label="Ignore these layers" label-size="sm" class="mb-2">
					<b-form-checkbox-group v-model="layer.props.physics.collisionBlacklist" stacked @change="$emit('save')">
						<b-form-checkbox v-for="l in otherLayers" :key="l.id" :value="l.id">{{l.name}}</b-form-checkbox>
					</b-form-checkbox-group>
				</b-form-group>
			</template>
			<chat-layer-settings v-if="isChat" :settings="layer.props.settings" @save="queueSave" />
			<div v-else-if="layer.props.widgetType === 'camera-grid'">
				<b-form-checkbox v-model="layer.props.settings.excludeHost" @change="$emit('save')">
					Exclude host's own camera
				</b-form-checkbox>
				<!-- Unset (grids made before this option) counts as on. -->
				<b-form-checkbox :checked="layer.props.settings.hideSpeakers !== false" @change="setSetting('hideSpeakers', $event)">
					Hide people already shown in their own layer
				</b-form-checkbox>
			</div>
			<b-form-group v-else-if="layer.props.widgetType === 'speaker'" label="Participant" label-size="sm" class="mb-0">
				<b-form-select size="sm" :value="speakerValue" :options="speakerOptions" @change="onSpeaker" />
			</b-form-group>
			<b-form-group v-else label="Settings (JSON)" label-size="sm" class="mb-0" :state="jsonError ? false : null" :invalid-feedback="jsonError">
				<b-form-textarea size="sm" rows="10" class="text-monospace" :value="settingsJson" :state="jsonError ? false : null" @change="onSettingsJson" />
			</b-form-group>
		</template>
	</div>
</template>

<script>
import PipiPupuFile from '@/components/PipiPupuFile.vue';
import GoogleFontInput from '@/components/GoogleFontInput.vue';
import { isChatWidgetType } from '@/overlay/chatSettings';
import BrandColorInput from './BrandColorInput.vue';
import ChatLayerSettings from './ChatLayerSettings.vue';
import { ALL_WIDGET_TYPES } from './layerTypes';

const SAVE_DELAY_MS = 400;

export default {
	components: { PipiPupuFile, GoogleFontInput, BrandColorInput, ChatLayerSettings },
	props: {
		layer: { type: Object, required: true },
		overlay: { type: Object, required: true },
		otherLayers: { type: Array, default: () => [] },
		// Who a Speaker layer can point at: { value: 'callerId|deviceId', text } - only the call studio has any.
		participants: { type: Array, default: () => [] },
	},
	data() { return {
		jsonError: null,
		saveTimer: null,
		horizontalAligns: [
			{ value: 'left', icon: 'text-left', title: 'Align left' },
			{ value: 'center', icon: 'text-center', title: 'Align center' },
			{ value: 'right', icon: 'text-right', title: 'Align right' },
		],
		verticalAligns: [
			{ value: 'top', icon: 'align-top', title: 'Align top' },
			{ value: 'middle', icon: 'align-middle', title: 'Align middle' },
			{ value: 'bottom', icon: 'align-bottom', title: 'Align bottom' },
		],
		collisionModes: [
			{ value: 'above', text: 'Layers above this one' },
			{ value: 'blacklist', text: 'All layers except the ones I pick' },
		],
	}},
	computed: {
		isChat() { return isChatWidgetType(this.layer.props.widgetType); },
		widgetLabel() { return ALL_WIDGET_TYPES.find(w => w.value === this.layer.props.widgetType)?.label || this.layer.props.widgetType; },
		settingsJson() { return JSON.stringify(this.layer.props.settings || {}, null, 2); },
		speakerValue() { return `${this.layer.props.settings?.callerId || ''}|${this.layer.props.settings?.deviceId || ''}`; },
		speakerOptions() {
			const options = [ { value: '|', text: 'Choose a participant…' }, ...this.participants ];
			// A layer can outlive the person it points at (they left, or their row was cleaned up).
			if(this.layer.props.settings?.callerId && !options.some(o => o.value === this.speakerValue)) options.push({ value: this.speakerValue, text: 'Not in the call right now' });
			return options;
		},
	},
	beforeDestroy() {
		if(this.saveTimer) this.flushSave();
	},
	methods: {
		// Colour pickers and sliders fire continuously while dragged; save once they settle.
		queueSave() {
			clearTimeout(this.saveTimer);
			this.saveTimer = setTimeout(() => this.flushSave(), SAVE_DELAY_MS);
		},
		flushSave() {
			clearTimeout(this.saveTimer);
			this.saveTimer = null;
			this.$emit('save');
		},
		// For props a layer saved before the field existed doesn't have a key for yet.
		setProp(key, value) {
			this.$set(this.layer.props, key, value);
			this.$emit('save');
		},
		// Like setProp, but for a key of the widget's own settings.
		setSetting(key, value) {
			this.$set(this.layer.props.settings, key, value);
			this.$emit('save');
		},
		onSpeaker(value) {
			const [ callerId, deviceId ] = value.split('|');
			this.$set(this.layer.props.settings, 'callerId', callerId || null);
			this.$set(this.layer.props.settings, 'deviceId', deviceId || null);
			// A layer still called by its default name takes the participant's.
			const chosen = this.participants.find(p => p.value === value);
			if(chosen && this.layer.name === 'Speaker') this.layer.name = chosen.text;
			this.$emit('save');
		},
		onFile(file) {
			this.$set(this.layer.props, 'file', file || null);
			this.layer.props.src = file?.url || '';
			this.$emit('source');
		},
		onSettingsJson(text) {
			try {
				this.layer.props.settings = JSON.parse(text || '{}');
				this.jsonError = null;
				this.$emit('save');
			} catch(e) {
				this.jsonError = `Invalid JSON: ${e.message}`;
			}
		},
	},
};
</script>
